"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function (o, m, k, k2) {
    if (k2 === undefined)
        k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
        desc = { enumerable: true, get: function () { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function (o, m, k, k2) {
    if (k2 === undefined)
        k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function (o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function (o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function (o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o)
                if (Object.prototype.hasOwnProperty.call(o, k))
                    ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule)
            return mod;
        var result = {};
        if (mod != null)
            for (var k = ownKeys(mod), i = 0; i < k.length; i++)
                if (k[i] !== "default")
                    __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.AttributeTagsEditCell = void 0;
const react_1 = __importStar(require("react"));
const ui_components_1 = require("@groupby/ui-components");
const client_constants_1 = require("../../../../../../client/client.constants");
const product_table_1 = require("../../../../../../common/product-table");
const attributes_1 = require("../../../../../../constants/attributes");
const generic_1 = require("../../../../../../generic");
const use_product_details_values_1 = require("../../../../../product-details-dialog/product-details-attributes/product-details-attribute/client/use-product-details-values");
const localization_1 = require("../../../../../../../localization");
const store_1 = require("../../../../../../../store");
/**
 * Edit cell component for AI-enriched attribute tags in new MUI DataGrid.
 *
 * This component provides:
 * - Inline tag editing using UI Components TagsEditCell
 * - Auto-complete with existing attribute values
 * - GraphQL mutation integration for saving changes
 * - Automatic query refetching after updates
 *
 * **Usage:** Used in new table's `renderEditCell` for AI-enriched attribute columns.
 * Provides the editing interface when users double-click or press Enter on a tag cell.
 *
 * **Business Logic:**
 * - Saves changes via `useChangeAttributeValues` hook
 * - Refetches product list after successful updates which naturally cascades to counters
 *
 * @example
 * ```tsx
 * // In column definition
 * {
 *   field: 'labels.color',
 *   renderEditCell: (params) => (
 *     <AttributeTagsEditCell
 *       cellId={params.id}
 *       fieldName={params.field}
 *       value={params.value || []}
 *       row={params.row}
 *       attributeLabel="color"
 *     />
 *   )
 * }
 * ```
 */
const AttributeTagsEditCell = ({ cellId, fieldName, value, row, attributeLabel, productTreeNodeId, options = [], autoOpen, onSubmit, }) => {
    const selectedCatalogId = (0, store_1.useSelectedCatalogId)();
    const { formatMessage: t } = (0, localization_1.useLocalization)();
    const apiRef = (0, ui_components_1.useAdvancedGridApiContext)();
    // Capture value at mount time. This component only exists during edit mode,
    // so the mount-time value is always correct. Background refetches may later
    // update the rows prop and cause re-renders with a stale/different value —
    // ignoring those prevents content flickering inside the popover.
    const initialValueRef = (0, react_1.useRef)(value);
    // Fetch attribute value options from backend
    const { loadProductDetailsValues, data: backendOptions = [], isLoading: isLoadingOptions, pagination: { hasNextPage, onNextPage, }, onSearch, } = (0, use_product_details_values_1.useProductsDetailsValues)({
        productTreeNodeId: row.productTreeNodeId,
        catalogId: selectedCatalogId,
        attributeName: attributeLabel,
        pageSize: client_constants_1.PAGE_SIZE_LARGE,
        options: { fetchPolicy: 'cache-first' },
    });
    // Auto-fetch options when the cell is loaded
    (0, react_1.useEffect)(() => {
        if (productTreeNodeId && selectedCatalogId && attributeLabel && row.productTreeNodeId) {
            void loadProductDetailsValues();
        }
    }, [loadProductDetailsValues, productTreeNodeId, selectedCatalogId, attributeLabel, row.productTreeNodeId]);
    // Transform backend string[] to TagOption[] format and combine with current values plus special values
    const derivedOptions = (0, react_1.useMemo)(() => {
        if (options.length > 0) {
            return options;
        }
        const optionsSet = new Set();
        const translatedNotApplicable = t({ key: attributes_1.SpecialAttributeValueToLocalizationKey[attributes_1.SpecialAttributeValue.NotApplicable] });
        initialValueRef.current.forEach((tag) => {
            if (tag !== translatedNotApplicable) {
                optionsSet.add(tag);
            }
        });
        if (!isLoadingOptions) {
            backendOptions.forEach((option) => optionsSet.add(option));
        }
        const finalOptions = [{
                value: translatedNotApplicable,
                label: translatedNotApplicable,
                pinned: true,
            }, ...Array.from(optionsSet).map((tag) => ({ value: tag, label: tag }))];
        return finalOptions;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `initialValueRef.current` is
        // intentionally omitted: it holds the value captured at mount time to prevent flickering
        // when background refetches update the `value` prop while the edit popover is open.
    }, [options, backendOptions, isLoadingOptions, t]);
    // Callback to determine chip type - Unknown values get special red styling
    const getChipType = (0, react_1.useCallback)((chipValue) => (chipValue === attributes_1.SpecialAttributeValue.Unknown
        ? ui_components_1.ChipType.lightred
        : ui_components_1.ChipType.blue), []);
    const handleChange = (0, react_1.useCallback)((_id, newTags) => __awaiter(void 0, void 0, void 0, function* () {
        // Not Applicable value was transformed into a shorter form for display in the table
        // TODO: add support for passing both label and value as the chip's value
        const specialAttributeValueLabel = t({ key: attributes_1.SpecialAttributeValueToLocalizationKey[attributes_1.SpecialAttributeValue.NotApplicable] });
        const normalize = (tags) => Array.from(new Set(tags))
            .filter((v) => v !== attributes_1.SpecialAttributeValue.Unknown);
        const normalizedNew = normalize(newTags);
        const { processedNewValues } = (0, product_table_1.getAttributeValueChanges)(normalizedNew, initialValueRef.current, specialAttributeValueLabel);
        yield apiRef.current.setEditCellValue({ id: row.id, field: fieldName, value: processedNewValues }, { debounceMs: 0 });
        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `initialValueRef.current` is
        // intentionally omitted: it holds the value captured at mount time; the callback should
        // always use the original value, not one updated by background refetches.
    }), [apiRef, fieldName, row.id, t, onSubmit]);
    return (react_1.default.createElement(ui_components_1.TagsEditCell, { cellId: cellId, fieldName: fieldName, value: initialValueRef.current, options: derivedOptions, onChange: handleChange, areOptionsLoading: isLoadingOptions, onLoadMore: onNextPage, onSearch: onSearch, hasNextPage: hasNextPage, searchDebounceMs: 500, loadingMoreIndicator: react_1.default.createElement(generic_1.LoadingIndicator, { className: "loading-indicator" }), loadingIndicator: react_1.default.createElement(generic_1.LoadingIndicator, { className: "loading-indicator" }), getChipType: getChipType, isRemoveAllowed: false, autoOpen: autoOpen }));
};
exports.AttributeTagsEditCell = AttributeTagsEditCell;
